import React, {
  useEffect, useState, useMemo,
} from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormControlLabel, Link, Paper, Checkbox, useTheme, useMediaQuery, makeStyles,
  Select,
} from '@material-ui/core';
import {
  Contacts, Star, CheckCircle, WarningRounded, RadioButtonUnchecked, Store, Apartment, Edit, Lock,
  LocationOn,
} from '@material-ui/icons';
import BusinessIcon from '@material-ui/icons/Business';
import ApartmentIcon from '@material-ui/icons/Apartment';
import { useMaestroDataSource, usePermissions, useTranslation } from '@michelin/central-provider';
import { getOptions, SelectOption } from '@michelin/select-options-provider';
import {
  ValueAvatar,
  Container,
  FilterGroup,
  useListSelectFilter,
  GlobalActions,
  Tooltip,
  LoadingBounce,
  Fab,
} from '@michelin/acid-components';
import { ThemeType } from '@michelin/acid-components/build/types/ThemeType';
import useURLHashParams from '@michelin/acid-components/build/components/ListingFilters/hashParameters';
import DataGrid, { SearchPanel, Column } from 'devextreme-react/data-grid';
import { Template } from 'devextreme-react/core/template';
import moment from 'moment-timezone';
import {
  ChangeLogEntities,
  LocationBT,
  PossibleValidations,
  ProviderLocation,
} from 'Types';
import { locationsList } from 'Types/APITypes';
import { useLastUpdateLocations } from 'hooks/useLastModified';
import { useLocationBT } from 'hooks/useLocation';
import { PlainMessage } from 'components/PlainMessage';
import { CSSProperties } from 'styled-components';
import ContactsListModal from '../Contact/ListModal';
import { getKeys } from '../../Utils';
import SpeedDials, { SpeedDialsActions } from 'components/SpeedDial';
import { OverflowMenu } from "../OverflowMenu";
import {useGetB2BDealerLocatorFile} from "../../hooks/useAdminQueries";
import LastModified from 'components/ChangeLog/LastModified';
import GLOBAL_CONFIG from '../../global-variables';
import { ProfileMenuItemProps } from '@michelin/central-provider/dist/components/ProfileBox/ProfileMenuItem';
import { PageTitle } from 'Utils/CanvasPageTitle';
import { runningOnCanvas } from '@michelin/central-provider/dist/utils/canvas';

interface LocationListProps {
  action: string,
  locationTypes: SelectOption[],
  btLocation: LocationBT,
}

const VALIDATION_ERROR_COLOR = 'rgb(241, 111, 111)';
const VALIDATION_SUCCESS_COLOR = 'rgb(51, 153, 51)';

const TABLE_HEIGHT = `calc(100vh - ${runningOnCanvas() ? '100px' : '199px'})`;

const useValidationTooltipStyles = makeStyles((theme: ThemeType) => ({
  validatedTooltip: {
    backgroundColor: VALIDATION_SUCCESS_COLOR,
    color: theme.palette.primary.contrastText,
  },
  validatedArrow: { color: theme.palette.primary.main },
  nonValidatedTooltip: {
    backgroundColor: VALIDATION_ERROR_COLOR,
    color: theme.palette.primary.contrastText,
  },
  nonValidatedArrow: { color: VALIDATION_ERROR_COLOR },
}));

const validationLabels = {
  brands_sold: 'Brands Sold',
  eligible_for_dealer_locator: 'Eligible for Locator',
  has_gps_location: 'GPS Location',
  has_hours_of_operation: 'Store Hours Loaded',
  is_valid_phone_number: 'Phone Number',
  is_valid_physical_address: 'Physical Address',
  open_for_business: 'Open for Business',
  retail_location_flag: 'Retail Location',
  is_valid_service_address: 'Service Address',
  store_hours_loaded: 'Store Hours Loaded',
  has_mechanical_services: 'Mechanical Services',
  has_mechanics: 'Mechanical Technicians Added',
  open_for_commercial_business: 'Open for Business',
  phone_number: 'Phone Number',
  minimum_purchase_requirements: {
    true: 'Minimum Purchase Requirements',
    false: 'Minimum Purchase Requirements ({{minimum_purchase_requirements}} missing)',
  }
};

function LocationList({ action, locationTypes, btLocation }: LocationListProps) {
  const { btNumber } = useParams<{ btNumber: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const redirectTarget = new URLSearchParams(location.search).get('redirect');
  const hashParams = useURLHashParams();
  const permissions = usePermissions();
  const theme = useTheme();
  const tabletView = useMediaQuery(theme.breakpoints.down(800));
  const cellView = useMediaQuery(theme.breakpoints.down(500));

  const [mode, setMode] = useState(hashParams.getHashValue('locType') === 'AAD' ? 'AAD' : 'LT');
  const [listContacts, setListContacts] = useState<string>('');

  const locationOnRetailLocators = [
    { label: t('On Retail Locator'), value: 'retail:true' },
    { label: t('Not on Retail Locator'), value: 'retail:false' },
  ];

  const locationOnCommercialMechanicalLocators = [
    { label: t('On Commercial Mechanical Locator'), value: 'mechanical:true' },
    { label: t('Not on Commercial Mechanical Locator'), value: 'mechanical:false' },
  ];

  const locationOnCommercialTireLocators = [
    { label: t('On Commercial Tire Locator'), value: 'tire:true' },
    { label: t('Not on Commercial Tire Locator'), value: 'tire:false' },
  ];

  const b2bWebsiteFile = useGetB2BDealerLocatorFile();

  const lastUpdate = useLastUpdateLocations(btNumber);

  const typeFilter = useListSelectFilter({
    label: t('Location Type'),
    options: locationTypes,
    hashParam: 'locType',
    unselectedLabel: t('All Locations'),
  });

  const enableRetailOnLocator =
    (btLocation.has_aad_locations || btLocation.has_lt_locations)
    && ['', 'LT', 'AAD'].includes(typeFilter.value || '');
  const enabledRetailMassSTUpdate =
    (btLocation.count_own_aad_locations > 1 || btLocation.has_lt_locations)
    && ((btLocation.count_own_aad_locations > 1 && typeFilter.value === 'AAD') || ['', 'LT'].includes(typeFilter.value || ''));
  const enableCommercialMechanicalOnLocator =
    (btLocation.has_commercial_locations)
    && ['', 'commercial'].includes(typeFilter.value || '');
  const enableCommercialTireOnLocator =
    (btLocation.has_commercial_locations || btLocation.has_tad_locations || btLocation.has_kad_locations || btLocation.has_oliver_locations)
    && ['', 'commercial', 'TAD', 'KAD', 'oliver'].includes(typeFilter.value || '');
  const enabledCommercialMassSTUpdate =
    (btLocation.has_commercial_locations || btLocation.count_own_tad_locations > 1 || btLocation.has_kad_locations || btLocation.has_oliver_locations)
    && ((btLocation.count_own_tad_locations > 1 && typeFilter.value === 'TAD') || ['', 'commercial', 'oliver'].includes(typeFilter.value || ''));
  const enabledTechnicianServiceAreaUpdate = (btLocation.has_commercial_locations || btLocation.has_tad_locations || btLocation.has_kad_locations || btLocation.has_oliver_locations)
  const permissionTechSerAreaRead = permissions.allowsAction('technician_service_area.read');
  const locationOnLocators = [
    ...(enableRetailOnLocator ? locationOnRetailLocators : []),
    ...(enableCommercialMechanicalOnLocator ? locationOnCommercialMechanicalLocators : []),
    ...(enableCommercialTireOnLocator ? locationOnCommercialTireLocators : []),
    // { label: t('On no Locators'), value: 'retail:false,mechanical:false,tire:false' },
  ];

  const onLocatorFilter = useListSelectFilter({
    label: t('On Locator'),
    options: locationOnLocators,
    unselectedLabel: t("Don't filter"),
    hashParam: 'onLocator',
  });

  useEffect(() => {
    setMode(typeFilter.value || '');
  }, [setMode, typeFilter.value]);

  if (onLocatorFilter.value && !locationOnLocators.some(option => option.value === onLocatorFilter.value)) {
    hashParams.setHashValue('onLocator', undefined);
  }

  if (!permissions.allowsAction('update_open_for_business.update') && action === 'edit') {
    history.push(`/${btNumber}`);
  }

  const filterGroup = useMemo(() => new FilterGroup([
    typeFilter.filter,
    onLocatorFilter.filter,
  ]), [typeFilter.filter, onLocatorFilter.filter]);

  const ds = useMaestroDataSource({
    API: locationsList(btNumber),
    reqOptions: {
      filters: {
        combined: 'true',
        type: typeFilter.value || undefined,
        on_locator: onLocatorFilter.value,
      },
    },
    componentPlacements: {
      filtersPlacement: tabletView ? 'outside' : 'embeded',
      searchPlacement: cellView ? 'outside' : 'embeded',
    },
    filterGroup,
    preloadNextPage: true,
  });

  const RenderContactsModal = () => {
    if (!listContacts) return <></>;

    if (!permissions || !permissions.location) return <></>;

    const contactStNumber = listContacts;
    const contactStStoreNumber = '';

    const contactBtNumber = permissions.location.customer_number || '';
    const contactBtName = permissions.location.customer_dba_name || permissions.location.customer_name || '';
    let title = 'Contacts: ';

    // TODO: Review what needs to be displayed for AADs

    title += t('BT: {{btName}} (#{{btNumber}}) - ST/AAN #{{stNumber}} - Store #{{storeNumber}}', {
      btName: (contactBtName || '').replace("'", '´'),
      btNumber: contactBtNumber,
      stNumber: contactStNumber,
      storeNumber: contactStStoreNumber,
    });

    return (
      <ContactsListModal
        onClose={() => {
          setListContacts('');
        }}
        title={title}
        stNumber={listContacts}
        btNumber={btNumber}
        contactLevel={'All'}
      />
    );
  };

  const renderContacts = (loc: ProviderLocation): JSX.Element => {
    if (!loc.contacts_count) return <ValueAvatar color="default" icon={<Contacts />} value={loc.contacts_count} disabled />;

    return (
      <div
        onClick={() => setListContacts(loc.id)}
        style={{ cursor: 'pointer' }}
      >
        <ValueAvatar
          color="primary"
          icon={<Contacts />}
          value={loc.contacts_count}
          tooltip={t("Contact's list")}
        />
      </div>
    );
  };

    const renderTechnician = (loc:ProviderLocation): JSX.Element => {
    const  { associated_location_type } = loc;
    const allowTechnicianServiceArea = associated_location_type.commercial || associated_location_type.tad || associated_location_type.kad || associated_location_type.oliver;
    const stNumber = loc.ship_to.ship_to_number || ''
    if (!allowTechnicianServiceArea || !permissionTechSerAreaRead)
      return (
        <></>
      );
   if ((loc.secondary_st_locations?.length || 0)===0)
      return (
        <ValueAvatar
          color="default"
          icon={<LocationOn />}
          value={0}
          disabled
        />
      );
    return (  
      <div
        onClick={() => {
          history.push(`/${btNumber}/technicianServiceArea/${stNumber}/view`)
        }}
        style={{ cursor: "pointer" }}
      >
        <ValueAvatar
          color="primary"
          icon={<LocationOn />}
          value={loc.secondary_st_locations?.length || 0}
          tooltip={t("Secondary ST Locations")}
        />
      </div>
    );
  };

  const ShipToInfo = ({ ship_to, headquarters_flag, associated_location_type, primary_servicing_distributor, mdm }: ProviderLocation): JSX.Element => {
    const operationMode = associated_location_type.lt || associated_location_type.aad ? 'retail' : 'commercial';
    const headQuartersFlagStyle: CSSProperties = { color: 'rgb(249, 198, 55)', marginLeft: -24, height: 16, verticalAlign: 'bottom' };

    return (
      <div
        style={{ cursor: 'pointer', minWidth: 170, paddingLeft: 8 }}
        onClick={() => {
          if (redirectTarget === 'tech-rotation') {
            history.push(`/${btNumber}/tech-rotation/${ship_to.ship_to_number}`);
          } else {
            history.push(`/${btNumber}/${operationMode}/${ship_to.ship_to_number}/location/view`);
          }
        }}
      >
        {headquarters_flag ? <Tooltip placement="top" arrow title={t('Headquarters') || ''} ><Star style={headQuartersFlagStyle} /></Tooltip> : null}
        <Link><b>{ship_to.dba_name || ship_to.name || ''}</b></Link>
        {ship_to.name && <br />}
        {`${t('Ship to #')} `}
        <b>{(GLOBAL_CONFIG.useMdmIds && mdm?.site_id) || ship_to.ship_to_number}</b>
        {ship_to.store_number && <div>{`${t('Store #')} `}<b>{ship_to.store_number}</b></div>}
        {associated_location_type?.aad && primary_servicing_distributor && <div style={{ opacity: 0.5 }}><Tooltip placement="top" arrow title={t('AAD Primary Servicing Distributor') || ''} ><BusinessIcon style={headQuartersFlagStyle} /></Tooltip>BT # <b>{primary_servicing_distributor?.bill_to_number || 'N/A'}</b> {primary_servicing_distributor?'-':''} {primary_servicing_distributor?.dba_name || primary_servicing_distributor?.name || ''}</div>}
        {associated_location_type?.tad && primary_servicing_distributor && <div style={{ opacity: 0.5 }}><Tooltip placement="top" arrow title={t('TAD Primary Servicing Distributor') || ''} ><ApartmentIcon style={headQuartersFlagStyle} /></Tooltip>BT # <b>{primary_servicing_distributor?.bill_to_number || 'N/A'}</b> {primary_servicing_distributor?'-':''} {primary_servicing_distributor?.dba_name || primary_servicing_distributor?.name || ''}</div>}
        {associated_location_type?.kad && primary_servicing_distributor && <div style={{ opacity: 0.5 }}><Tooltip placement="top" arrow title={t('KAD Primary Servicing Distributor') || ''} ><ApartmentIcon style={headQuartersFlagStyle} /></Tooltip>BT # <b>{primary_servicing_distributor?.bill_to_number || 'N/A'}</b> {primary_servicing_distributor?'-':''} {primary_servicing_distributor?.dba_name || primary_servicing_distributor?.name || ''}</div>}
      </div>
    );
  };

  const renderCity = (loc: ProviderLocation) => {
    let country = (loc.country || '').toUpperCase();
    switch (country) {
      case 'US': country = 'UNITED STATES'; break;
      case 'CA': country = 'CANADA'; break;
      case 'MX': country = 'MEXICO'; break;
      default: break;
    }
    return <>
      {`${loc.city}, ${loc.state} ${loc.zip || ''}`}
      <br />
      {country}
    </>
  };

  const renderAsociatedLocationTypes = ({ associated_location_type }: ProviderLocation) => {
    return <div style={{ zoom: 0.8 }} >
      {associated_location_type.lt &&
        <div><FormControlLabel label={t('Retail Passenger / LT')} control={(<Checkbox checked disabled />)} /></div>
      }
      {associated_location_type.aad &&
        <div><FormControlLabel label={t('AAD')} control={(<Checkbox checked disabled />)} /></div>
      }
      {associated_location_type.kad &&
        <div><FormControlLabel label={t('KAD')} control={(<Checkbox checked disabled />)} /></div>
      }
      {associated_location_type.tad &&
        <div><FormControlLabel label={t('TAD')} control={(<Checkbox checked disabled />)} /></div>
      }
      {associated_location_type.commercial &&
        <div><FormControlLabel label={t('Commercial Truck')} control={(<Checkbox checked disabled />)} /></div>
      }
      {associated_location_type.oliver &&
        <div><FormControlLabel label={t('Oliver')} control={(<Checkbox checked disabled />)} /></div>
      }
    </div>
  };

  const ValidatedItems = ({ validated, text, validations, extraData, overridden }: { validated: boolean, text: string, validations: PossibleValidations, overridden?:boolean, extraData?: { [key: string]: string } }) => {
    const icnStyle: CSSProperties = { fontSize: '1em', marginLeft: -15, verticalAlign: 'bottom', marginBottom: 1 };
    const tooltipIcnStyle: CSSProperties = { marginRight: 4, fontSize: '1em', verticalAlign: 'bottom', marginBottom: 1 };
    const { palette }: ThemeType = useTheme();
    const style: CSSProperties = {
      color: (validated ? VALIDATION_SUCCESS_COLOR : VALIDATION_ERROR_COLOR),
      marginLeft: 16,
      width: 200,
      marginBottom: 4,
      marginTop: 4,
      fontSize: '1.1em',
    };
    const classes = useValidationTooltipStyles();

    const getValidationLabel = (key: keyof PossibleValidations, value: boolean) => {
      if (!validationLabels[key]) return key;
      if (typeof validationLabels[key] === 'string') return validationLabels[key];
      const label: { true: string, false: string } = validationLabels[key] as any;
      return value ? label.true : label.false;
    }

    return (
      <div style={style}>
        <Tooltip
          interactive
          arrow
          placement="left"
          classes={{
            tooltip: validated ? classes.validatedTooltip : classes.nonValidatedTooltip,
            arrow: validated ? classes.validatedArrow : classes.nonValidatedArrow,
          }}
          title={(<div style={{ margin: 8, fontSize: 14 }}>
            {getKeys(validations).map((key) => (
              <div key={key} style={{ marginTop: 4, marginBottom: 4 }}>
                {validations[key] ? <CheckCircle style={tooltipIcnStyle} /> : <RadioButtonUnchecked style={tooltipIcnStyle} />}
                {t(getValidationLabel(key, validations[key]), extraData)}
              </div>
            ))}
            {overridden &&
              <div style={{ marginTop: 8, marginBottom: 4, fontSize:13, fontStyle: 'italic' }}>
                {t(`Note: On Locator flag for this location is being overwritten by an administrator.`)}
              </div>
            }
          </div>
          )
          }>
          {validated ? <CheckCircle style={icnStyle} /> : <WarningRounded style={icnStyle} />}
        </Tooltip>
        {` ${text}`}
      </div>
    );
  };

  const renderDealerLocatorStatus = ({ dealer_locator_status }: ProviderLocation): JSX.Element => {
    return <>
      {dealer_locator_status.commercial_tire_retread && <ValidatedItems
        validations={dealer_locator_status.commercial_tire_retread.validations}
        validated={dealer_locator_status.commercial_tire_retread.status}
        extraData={dealer_locator_status.commercial_tire_retread.extra}
        overridden={dealer_locator_status.commercial_tire_retread.overridden}
        text={t('Commercial Tire/Retread Locator')} />}
      {dealer_locator_status.commercial_mechanical && <ValidatedItems
        validations={dealer_locator_status.commercial_mechanical.validations}
        validated={dealer_locator_status.commercial_mechanical.status}
        extraData={dealer_locator_status.commercial_mechanical.extra}
        overridden={dealer_locator_status.commercial_mechanical.overridden}
        text={t('Commercial Mechanical Locator')} />}
      {dealer_locator_status.lt && <ValidatedItems
        validations={dealer_locator_status.lt.validations}
        validated={dealer_locator_status.lt.status}
        extraData={dealer_locator_status.lt.extra}
        overridden={dealer_locator_status.lt.overridden}
        text={t('Retail Passenger/LT Locator')} />}
    </>;
  };

  const renderLastModified = (update_date: string, id: string): JSX.Element | null => {
    if (!update_date) return null;
    const updated_date = moment(update_date).tz('America/New_York').format('YYYY-MM-DD HH:mm:ss');
    const data = (<div style={{ width: 80, zoom: 0.8 }}>{`${updated_date} EST`}</div>);

    if (!permissions.allowsAction('change_history.read')) return data;

    return (
      <Link style={{ cursor: 'pointer' }} onClick={() => history.push(`/${btNumber}/change-history/${id}/list#entity=${ChangeLogEntities.locationInformation}`)}>
        {data}
      </Link>
    );
  };

  const renderActionButtons = () => {
    const actions: SpeedDialsActions[] = [];

    if (permissions.allowsAction('mass_st_update.update')) {
      if (enabledRetailMassSTUpdate) {
        actions.push({
          icon: <Store />,
          title: t('Retail - Mass ST Update'),
          key: 'retail-massstupdate',
          onClick: () => history.push(`/${btNumber}/massSTUpdate/services`),
        });
      }
      if (enabledCommercialMassSTUpdate) {
        actions.push({
          icon: <Apartment />,
          title: t('Commercial - Mass ST Update'),
          key: 'commercial-massstupdate',
          onClick: () => history.push(`/${btNumber}/massSTUpdate/commercial/location-information/list`),
        });
      }
    }
    if (enabledTechnicianServiceAreaUpdate && permissionTechSerAreaRead) {
      actions.push({
        icon: <LocationOn />,
        title: t("Technician Service Area Update"),
        key: "technician-service-area-update",
        onClick: () =>{
          history.push(`/${btNumber}/technicianServiceArea/view`)
        }
      });
    }
    if(actions.length === 0) return false;

    if(actions.length === 1 && actions[0].icon) return (
      <GlobalActions>
        <Fab
          color="primary"
          aria-label={actions[0].title}
          onClick={actions[0].onClick}
        >
          {actions[0].icon ?? <span />}
        </Fab>
      </GlobalActions>
    );
    return (
      <GlobalActions>
        <SpeedDials direction="up" actions={actions} icon={<Edit />} />
      </GlobalActions>
    );
  }

  const noDataText = t('0 locations found...');

  const overflowMenuItems: ProfileMenuItemProps[] | undefined = [];

  if(permissions.allowsAction('admin_features.self_service_promo_upload')) overflowMenuItems.push({
    label: t('Self Service Dealer Promo Upload'),
    icon: Lock,
    action: () => history.push(`/${btNumber}/admin/self-service-promo-upload`),
  });

  if(permissions.allowsAction('admin_features.b2b_website_extract_download') && (!enableRetailOnLocator || enableCommercialTireOnLocator) && !b2bWebsiteFile.isLoading && b2bWebsiteFile.data && b2bWebsiteFile.data.exists) overflowMenuItems.push({
    label: t('Download B2B Website Dealer Locator File'),
    icon: Lock,
    action: () => window.open(b2bWebsiteFile.data.url),
  });

  return (
    <>
      <PageTitle
        title={t('Locations summary')}
        rightItems={(
          <div style={{height:40}}>
            <div style={{position:'absolute', right: runningOnCanvas() ? 24 : 12, top:10}}>
              <div style={{minWidth:200, float: 'left', marginRight: runningOnCanvas() ? 8 : undefined}}>
                {overflowMenuItems.length
                  ? (
                    <OverflowMenu
                      style={{ marginTop: 4, marginLeft: 8 }} 
                      menuItems={overflowMenuItems}
                      title={t('Admin Menu')}
                      float="right"
                    />
                  )
                  : null}
                  <LastModified entity={ChangeLogEntities.btLocation} />
              </div>
            </div>
          </div>
        )}
      />
      <Container>
        <RenderContactsModal />
        <Paper>
          <div>
            {tabletView ? ds.renderFilterFields(null, { margin: 10 }) : undefined}
            {cellView ? <div style={{ marginBottom: -10, textAlign: 'right' }}>{ds.renderResultsCounter()}</div> : undefined}
          </div>
          {useMemo(() => (
            <DataGrid
              remoteOperations
              scrolling={{
                mode: 'virtual',
                rowRenderingMode: 'virtual',
              }}
              className="freespaced-table"
              dataSource={ds.dataSource}
              allowColumnReordering
              columnAutoWidth
              noDataText={noDataText}
              allowColumnResizing
              onToolbarPreparing={ds.onToolbarPreparing}
              style={{ height: TABLE_HEIGHT }}
              onEditorPreparing={(e: any) => {
                if (e.parentType === 'searchPanel') {
                  e.updateValueTimeout = 1000;
                }
              }}
            >
              <Template name="filterFields" render={ds.renderFilterFields} />
              <SearchPanel visible highlightSearchText={false} />
              <Template name="resultsCounter" render={ds.renderResultsCounter} />
              {permissions.allowsAction('contacts.list')
                ? (
                  <Column
                    caption={t('Contacts')}
                    cellRender={({ row }) => renderContacts(row.data)}
                    alignment={ 'center'}
                  />
                )
                : null}
              {enabledTechnicianServiceAreaUpdate && permissionTechSerAreaRead ? (
                  <Column
                    caption={t("Technician Service Area")}
                    headerCellRender={() => (
                      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column' }}>
                        <span>{t("Techserv-l1")}</span>
                        <span>{t("Techserv-l2")}</span>
                      </div>
                    )}
                    cellRender={({ row }) => renderTechnician(row.data)}
                    alignment={ 'center'}
                  />
              ) : null}
              <Column
                caption={t('Ship to')}
                cellRender={({ row }) => (<ShipToInfo {...row.data} />)}
                allowSorting
                dataField="display_name"
                defaultSortOrder="asc"
              />
              <Column
                caption={t('City, State/Province')}
                cellRender={({ row }) => renderCity(row.data)}
                dataField="city_state"
                allowSorting
              />
              <Column
                caption={t('Associated Location Types')}
                headerCellRender={() => (
                    <div
                      style={{
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {enabledTechnicianServiceAreaUpdate &&
                      permissionTechSerAreaRead ? (
                        <>
                          <span>{t("Assocloc-l1")}</span>
                          <span>{t("Assocloc-l2")}</span>
                        </>
                      ) : (
                        <span>{t("Associated Location Types")}</span>
                      )}
                    </div>
                  )}
                  cellRender={({ row }) =>
                    renderAsociatedLocationTypes(row.data)
                  }
              />
              <Column
                caption={t('Dealer Locator Status / Requirements')}
                cellRender={({ row }) => renderDealerLocatorStatus(row.data)}
              />
              <Column
                caption={t('Last modified')}
                cellRender={({ row }) => renderLastModified(row.data.update_date, row.data.id)}
                allowSorting
                dataField="update_date"
              />
            </DataGrid>
            // eslint-disable-next-line react-hooks/exhaustive-deps
          ), [onLocatorFilter.value, mode, typeFilter.value, typeFilter.filter, tabletView, cellView])}
        </Paper>
        {renderActionButtons()}
      </Container>
    </>
  );
}

function LocationListLoader({ action }: { action: string }) {
  const { btNumber } = useParams<{ btNumber: string }>();
  const locationQuery = useLocationBT(btNumber);
  const { t } = useTranslation();
  if (locationQuery.isError) {
    return <PlainMessage title={t('Error loading data')} messages={[t('Please try again later...')]} />;
  }
  if (locationQuery.isLoading || !locationQuery.data) {
    return <LoadingBounce style={{ height: '70vh' }} />;
  }
  const locationTypes = getOptions('spp_location_types');
  const filteredLocationTypes = locationTypes.filter((e) => (
    (e.value === 'LT' && locationQuery.data.has_lt_locations)
    || (e.value === 'AAD' && locationQuery.data.has_aad_locations)
    || (e.value === 'KAD' && locationQuery.data.has_kad_locations)
    || (e.value === 'TAD' && locationQuery.data.has_tad_locations)
    || (e.value === 'commercial' && locationQuery.data.has_commercial_locations)
    || (e.value === 'oliver' && locationQuery.data.has_oliver_locations)
  ));

  return <LocationList
    action={action}
    locationTypes={filteredLocationTypes}
    btLocation={locationQuery.data}
  />;
}

export {
  LocationListLoader as LocationList,
};
